import { Controller } from "@hotwired/stimulus";

// Live invoice totals. Computes each line's amount (quantity × price) and the
// running invoice total as the user types, so the money is visible before the
// invoice is saved — mirroring the totals shown on the invoice show page.
//
// Lines are added/removed by the dynamic-fields controller, which either
// appends a node or hides one (display:none) for a persisted record. A hidden
// line has no offsetParent, so it drops out of the sum on its own; a
// MutationObserver keeps the total in step with those DOM changes.
export default class extends Controller {
  static targets = [ "grandTotal" ];

  declare readonly grandTotalTarget: HTMLElement;
  declare readonly hasGrandTotalTarget: boolean;

  private observer?: MutationObserver;
  private readonly observeOptions: MutationObserverInit = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [ "style" ]
  };
  private readonly formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  });

  connect() {
    this.recompute = this.recompute.bind(this);
    this.element.addEventListener("input", this.recompute);

    // Watches for line items being added (node inserted) or removed (persisted
    // rows are hidden via a style change, new rows are detached).
    this.observer = new MutationObserver(this.recompute);
    this.observer.observe(this.element, this.observeOptions);

    this.recompute();
  }

  disconnect() {
    this.element.removeEventListener("input", this.recompute);
    this.observer?.disconnect();
  }

  recompute() {
    // Writing the totals below mutates elements inside the observed subtree,
    // which would re-trigger the observer and loop forever. Pause it around our
    // own writes, then re-arm to keep tracking real add/remove changes.
    this.observer?.disconnect();

    try {
      let grand = 0;

      this.element.querySelectorAll<HTMLElement>(".invoice-line-item").forEach((card) => {
        // A removed / hidden line has no layout box — skip it.
        if (card.offsetParent === null) return;

        const total = this.value(card, "quantity") * this.value(card, "price");
        grand += total;

        const out = card.querySelector<HTMLElement>(".invoice-line-item-total");
        if (out) out.textContent = this.formatter.format(total);
      });

      if (this.hasGrandTotalTarget) {
        this.grandTotalTarget.textContent = this.formatter.format(grand);
      }
    } finally {
      this.observer?.observe(this.element, this.observeOptions);
    }
  }

  private value(card: HTMLElement, field: string): number {
    const input = card.querySelector<HTMLInputElement>(`input[name$="[${field}]"]`);
    if (!input) return 0;
    const n = parseFloat(input.value.replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
}
